import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Link, Paper, Popper, useTheme } from '@mui/material';
import Segmented from './Segmented';
import EntryTypeHelp from './EntryTypeHelp';
import { useEngineStrings } from '../../user_guides/i18n/useEngineStrings';
import { entryTypeOptions, entryTypeCaptionSegments, MANAGE_PAYEES_PATH } from '../../utils/entryType';

/**
 * The row's Entry Type control (#1938, DESIGN_SYSTEM.md 8.15): the label, the
 * four-way full-word Segmented, the help icon, and a live caption under them.
 * One panel with the same content from every trigger: the entry's chip, and
 * the payee picker's option chevron (#1943).
 *
 * The caption names what Save writes for the type the ring is on. Hovering or
 * arrow-focusing a segment previews it: the ring moves there and the caption
 * follows; leaving returns both to the type in force (owner, 2026-09-30). The
 * caption comes from `prompt` ({ mode, payeeName, defaultType, value }), the
 * hook's entry or the flyout's per-option prompt; its `{manage}` placeholder
 * renders as a link to the Manage payees page in a new tab.
 *
 * `placement="float"` opens it below the chip (`anchorEl`), left edges aligned,
 * in a Popper kept in place (no portal) with fixed positioning: rendered right
 * after the chip, Tab goes chip, control, next field, and a fixed box adds
 * nothing to the table's scroll width, so the overflow probe cannot flip the
 * page to cards. `placement="side"` opens it beside the picker option
 * (`anchorEl` is that option), right of the list and vertically centered on
 * it, portaled so the list's own box never clips it. `placement="inline"`
 * renders it in flow (the mobile Sheet, cards, and a picker option on phones).
 *
 * It retracts (`onClose`) on a click or a keystroke outside it and on Escape.
 * The anchor, the payee dropdown and the help table count as inside, so a
 * click or a keystroke in another field closes it. The listeners start after
 * the commit that opened it, so the click that committed the payee never
 * closes it. With `keepFocus`, a mousedown inside it is prevented, so the
 * payee field keeps focus and the picker's list stays open while a segment
 * is clicked. With `autoFocus`, the checked segment takes focus on open (the
 * keyboard path from the picker).
 *
 * @param {object} props
 * @param {boolean} [props.open]          render or not (float and side: show the Popper)
 * @param {string} props.value            the selected entry type
 * @param {function} [props.onChange]     called with the newly selected value
 * @param {function} [props.onClose]      retract (outside click or key, Escape)
 * @param {object} [props.prompt]         caption inputs; null renders no caption
 * @param {('float'|'side'|'inline')} [props.placement]
 * @param {Element} [props.anchorEl]      the chip or the option: the anchor, and inside for the close rule
 * @param {boolean} [props.keepFocus]     prevent mousedown default inside (picker flyout)
 * @param {boolean} [props.autoFocus]     focus the checked segment when it opens
 * @param {object} [props.sx]             extra sx (inline placement only)
 */
const EntryTypeControl = ({
  open = true, value, onChange, onClose, prompt = null, placement = 'inline', anchorEl = null,
  keepFocus = false, autoFocus = false, sx,
}) => {
  const t = useEngineStrings();
  const theme = useTheme();
  const { ds, palette } = theme;
  const rootRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const floating = placement === 'float' || placement === 'side';
  const visible = floating ? open && Boolean(anchorEl) : open;
  const shown = preview ?? value;

  // A fresh open, or a new value in force, starts with no preview.
  useEffect(() => { setPreview(null); }, [visible, value, anchorEl]);

  // The keyboard path: the checked segment takes focus once the panel is in
  // the document. A floating panel mounts a render after it opens (the
  // Popper's portal), so the focus runs from the root's callback ref.
  const focusPendingRef = useRef(false);
  const focusChecked = (node) => node?.querySelector('[role="radio"][aria-checked="true"]')?.focus();
  useEffect(() => {
    focusPendingRef.current = visible && autoFocus;
    if (focusPendingRef.current && rootRef.current) {
      focusChecked(rootRef.current);
      focusPendingRef.current = false;
    }
  }, [visible, autoFocus]);
  const setRoot = useCallback((node) => {
    rootRef.current = node;
    if (node && focusPendingRef.current) {
      focusChecked(node);
      focusPendingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!visible || !onClose) return undefined;
    const inside = (node) => Boolean(node && (
      rootRef.current?.contains(node)
      || (anchorEl && typeof anchorEl.contains === 'function' && anchorEl.contains(node))
      || (typeof node.closest === 'function' && node.closest('.MuiAutocomplete-popper, [role="tooltip"]'))
    ));
    const onPointer = (e) => {
      if (!inside(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape' || !inside(e.target)) onClose();
    };
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('touchstart', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer, true);
      document.removeEventListener('touchstart', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [visible, onClose, anchorEl]);

  const segments = prompt ? entryTypeCaptionSegments(t, { ...prompt, value: shown }) : [];
  const rootProps = {
    ref: setRoot,
    'data-entry-type-control': 'true',
    ...(keepFocus ? { onMouseDown: (e) => e.preventDefault() } : {}),
  };

  const body = (
    <Box data-testid="entry-type-control" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <EntryTypeHelp label={t['entryType.label']}>
        <Segmented
          inCard
          ariaLabel={t['entryType.label']}
          options={entryTypeOptions(t)}
          value={value}
          previewValue={preview}
          onPreview={setPreview}
          onChange={onChange}
        />
      </EntryTypeHelp>
      {segments.length > 0 ? (
        <Box data-testid="entry-type-caption" sx={{ fontSize: '11px', lineHeight: 1.35, color: palette.text.secondary, maxWidth: 420 }}>
          {segments.map((seg, i) => (seg.manage ? (
            <Link key={i} href={MANAGE_PAYEES_PATH} target="_blank" rel="noopener" underline="hover">{seg.text}</Link>
          ) : (
            <span key={i}>{seg.text}</span>
          )))}
        </Box>
      ) : null}
    </Box>
  );

  if (floating) {
    const side = placement === 'side';
    return (
      <Popper
        open={visible}
        anchorEl={anchorEl}
        placement={side ? 'right' : 'bottom-start'}
        disablePortal={!side}
        popperOptions={{ strategy: 'fixed' }}
        modifiers={side ? [
          // Beside the highlighted option, clear of the list's edge; on the
          // left when the right has no room.
          { name: 'offset', options: { offset: [0, 10] } },
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { fallbackPlacements: ['left', 'bottom-start'] } },
        ] : [
          // Below the line being entered, clear of the row, over the saved rows.
          { name: 'offset', options: { offset: [0, 18] } },
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        ]}
        sx={{ zIndex: side ? theme.zIndex.modal + 1 : theme.zIndex.fab }}
      >
        <Paper
          {...rootProps}
          sx={{
            backgroundColor: palette.surface.overlay,
            backgroundImage: 'none',
            border: `1px solid ${palette.overlay.border}`,
            boxShadow: palette.overlay.shadow,
            borderRadius: `${ds.radius.container}px`,
            padding: '6px 10px 8px',
          }}
        >
          {body}
        </Paper>
      </Popper>
    );
  }

  if (!visible) return null;
  return <Box {...rootProps} sx={{ paddingTop: '2px', ...sx }}>{body}</Box>;
};

export default EntryTypeControl;
